import React from "react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Avatar,
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spacer,
  Text,
  useBreakpointValue,
  useColorMode,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { ToggleColorModeButton } from "../../theme/ToggleColorModeButton";
import { FaCog } from "react-icons/fa";
import { FiBell } from "react-icons/fi";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { getLogoUrl, ROLE_CONFIG } from "../Settings/constants";

const Header = () => {
  const sidebarWidth = useBreakpointValue({ base: "0", md: "250px" });
  const headerPadding = useBreakpointValue({ base: 2, md: 4 });
  const { colorMode } = useColorMode();
  const location = useLocation();
  const toast = useToast();
  const user = useSelector((state) => state.auth.loginData);
  const role = useSelector((state) => state.auth.role);
  const roleLabel = ROLE_CONFIG[role]?.label || role;

  const selectedItemBg = useColorModeValue("gray.200", "gray.700");
  const menuItemHoverBg = useColorModeValue("gray.200", "gray.600");
  const menuListBg = useColorModeValue("white", "gray.800");
  const menuListBorderColor = useColorModeValue("gray.200", "gray.600");
  const borderBottomHeaderColor = useColorModeValue("gray.300", "gray.600");
  const nameColor = useColorModeValue("gray.800", "whiteAlpha.900");
  const roleColor = useColorModeValue("gray.500", "gray.400");

  const notifyComingSoon = (feature) => {
    toast({
      title: `${feature} coming soon!`,
      description: "This feature is currently under development and will be available soon.",
      status: "info",
      duration: 4000,
      isClosable: true,
      position: "top-right",
    });
  };

  const utilityIconProps = {
    isRound: true,
    variant: "ghost",
    size: { base: "sm", md: "md" },
    m: { base: 1, md: 2 },
    _hover: { bg: selectedItemBg },
  };

  return (
    <Flex
      as="header"
      position="fixed"
      right="0"
      top="0"
      left={sidebarWidth}
      bg={colorMode === "light" ? "white" : "gray.800"}
      backdropFilter="saturate(180%) blur(5px)"
      px={headerPadding}
      py={2}
      align="center"
      boxShadow={"sm"}
      w={`calc(100% - ${sidebarWidth})`}
      height="4rem"
      borderBottomColor={borderBottomHeaderColor}
      borderBottomWidth="1px"
    >
      <Spacer />

      <Box>
        <ToggleColorModeButton
          position="relative"
          top={{ base: "2", md: "5" }}
          right={{ base: "2", md: "5" }}
          size={{ base: "sm", md: "md" }}
        />
      </Box>

      {/* Notifications */}
      <IconButton
        {...utilityIconProps}
        icon={<FiBell />}
        aria-label="Notifications"
        onClick={() => notifyComingSoon("Notifications")}
      />

      <Box
        display={{ base: "none", md: "block" }}
        h="24px"
        borderLeftWidth="1px"
        borderColor={borderBottomHeaderColor}
        mx={1}
      />

      {/* Profile menu — Settings lives inside here now */}
      <Menu>
        {({ isOpen }) => (
          <>
            <MenuButton
              as={Box}
              m={{ base: 1, md: 2 }}
              px={{ base: 2, md: 3 }}
              py="4px"
              borderRadius="full"
              bg={isOpen ? selectedItemBg : "transparent"}
              cursor="pointer"
              _hover={{ bg: selectedItemBg }}
            >
              <Flex align="center" gap={2}>
                <Avatar
                  size={{ base: "sm", md: "md" }}
                  src={getLogoUrl(user)}
                  name={user?.name || user?.username}
                />
                <Box display={{ base: "none", md: "block" }} lineHeight="1.2" textAlign="left">
                  <Text fontSize="sm" fontWeight="600" color={nameColor} noOfLines={1}>
                    {user?.name || user?.username || "User"}
                  </Text>
                  <Text fontSize="xs" color={roleColor} noOfLines={1}>
                    {roleLabel}
                  </Text>
                </Box>
                <ChevronDownIcon
                  boxSize={4}
                  color={roleColor}
                  transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
                  transition="transform 0.2s ease"
                />
              </Flex>
            </MenuButton>
            <Portal>
              <MenuList
                zIndex={1500}
                bg={menuListBg}
                borderColor={menuListBorderColor}
                borderRadius="12px"
                overflow="hidden"
                p={2}
              >
                <MenuItem
                  as={RouterLink}
                  to="/settings"
                  icon={<FaCog />}
                  borderRadius="8px"
                  bg={location.pathname === "/settings" ? selectedItemBg : "transparent"}
                  _hover={{ bg: menuItemHoverBg }}
                  _focus={{ bg: menuItemHoverBg }}
                >
                  Settings
                </MenuItem>
              </MenuList>
            </Portal>
          </>
        )}
      </Menu>
    </Flex>
  );
};

export default Header;
